import React from 'react';
import { FaCalendarAlt, FaMapMarkerAlt, FaRegClock, FaHome, FaTicketAlt, FaInfoCircle } from 'react-icons/fa';
import { getDisplayPrice, formatPrice } from '../../../../utils/priceUtils.js';
import ImageCard from './ImageCard.jsx';

const locationToString = (loc) => {
  if (!loc) return '';
  if (typeof loc === 'object') return loc.formatted_address || loc.street_address || '';
  if (typeof loc === 'string') {
    try {
      const parsed = JSON.parse(loc);
      return parsed.formatted_address || parsed.street_address || loc;
    } catch {
      return loc;
    }
  }
  return String(loc);
};

const ReadOnlyEventDetailsCard = ({ event, packages, activeTab, onTabChange, onSelectPackage, showSelectButton = true }) => {
  if (!event) return null;

  const formatTime = (timeString) => {
    if (!timeString || timeString === 'Time not specified') return 'Time not specified';
    
    try {
      let time;
      if (timeString.includes(':')) {
        const [hours, minutes] = timeString.split(':');
        time = new Date();
        time.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      } else {
        time = new Date(timeString);
      }
      
      if (Number.isNaN(time.getTime())) {
        return timeString;
      }
      
      const hours = time.getHours();
      const minutes = time.getMinutes();
      const period = hours >= 12 ? 'PM' : 'AM';
      
      const formattedHours = hours.toString().padStart(2, '0');
      const formattedMinutes = minutes.toString().padStart(2, '0');
      
      return `${formattedHours}:${formattedMinutes} ${period}`;
    } catch (error) {
      return timeString;
    }
  };

  const baseTabs = ['About', 'Packages', 'Terms & Conditions'];
  const dynamicTabs = Array.isArray(event.tab_sections) ? event.tab_sections : [];
  const tabs = [
    ...baseTabs,
    ...dynamicTabs.map((tab) => tab.name || 'Tab'),
  ];

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const parseDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const formatTooltipDate = (value) => {
    const d = parseDate(value);
    if (!d) return '';
    return d.toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const isPackageOnSale = (pkg) => {
    const start = parseDate(pkg.startDate);
    const end = parseDate(pkg.endDate);
    
    // Check if package is sold out using correct field names
    const soldTickets = pkg.soldTickets || pkg.sold_tickets || 0;
    const heldTickets = pkg.heldTickets || pkg.held_tickets || 0;
    const maxTickets = pkg.numberOfTickets || pkg.max_tickets || 0;
    const isSoldOut = (soldTickets + heldTickets) >= maxTickets && maxTickets > 0;
    
    if (!start || !end) {
      // If we don't have a full window, treat as available so we don't block purchases unexpectedly
      return true;
    }
    
    const isDateValid = start <= today && today <= end;
    
    return isDateValid && !isSoldOut;
  };

  return (
    <div className="event-details-card">
      <div className="event-details-card__info-bar">
        <div className="event-details-card__info-item">
          <FaCalendarAlt />
          <span>{event.date}</span>
        </div>
        <div className="event-details-card__info-item">
          <FaMapMarkerAlt />
          <span>{locationToString(event.location)}</span>
        </div>
        <div className="event-details-card__info-item">
          <FaRegClock />
          <span>{formatTime(event.time) || event.duration}</span>
        </div>
      </div>

      <div className="event-details-card__tabs">
        {tabs.map((label, index) => (
          <button
            key={label}
            type="button"
            className={
              index === activeTab
                ? 'event-details-card__tab event-details-card__tab--active'
                : 'event-details-card__tab'
            }
            onClick={() => onTabChange(index)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="event-details-card__tab-content">
        {activeTab === 0 && (
          <div>
            <h3>About This Event</h3>
            <p className="event-details-card__description">
              {event.description || event.details || 'No description available.'}
            </p>
            {event.date && (
              <div className="event-details-card__detail-row">
                <strong>Date:</strong> {event.date}
              </div>
            )}
            {event.time && (
              <div className="event-details-card__detail-row">
                <strong>Time:</strong> {formatTime(event.time)}
              </div>
            )}
            {locationToString(event.location) && (
              <div className="event-details-card__detail-row">
                <strong>Location:</strong> {locationToString(event.location)}
              </div>
            )}
          </div>
        )}

        {activeTab === 1 && (
          <div>
            <h3>Available Packages</h3>
            <div className="event-details-card__packages-grid">
              {packages && packages.length > 0 ? (
                packages.map((pkg, index) => {
                  console.log('🔍 ReadOnlyEventDetailsCard received props:', JSON.stringify({
                    packagesLength: packages?.length || 0,
                    packages: packages,
                    firstPackage: packages?.[0]
                  }, null, 2));

                  console.log('🔍 ReadOnlyEventDetailsCard package data:', JSON.stringify({
                    index,
                    packageType: pkg.packageType,
                    sold_tickets: pkg.sold_tickets,
                    max_tickets: pkg.max_tickets,
                    startDate: pkg.startDate,
                    endDate: pkg.endDate,
                    validFromDate: pkg.validFromDate,
                    validToDate: pkg.validToDate,
                    availableTickets: (pkg.max_tickets || 0) - (pkg.sold_tickets || 0),
                    fullPackageObject: pkg
                  }, null, 2));
                  
                  const onSale = isPackageOnSale(pkg);
                  const soldTickets = pkg.soldTickets || pkg.sold_tickets || pkg.SoldTickets || 0;
                  const heldTickets = pkg.heldTickets || pkg.held_tickets || 0;
                  const maxTickets = pkg.numberOfTickets || pkg.max_tickets || pkg.NumberOfTickets || pkg.max_tickets || 0;
                  
                  // TEMPORARY TEST: Force SC package to be sold out
                  const tempForceSoldOut = pkg.packageType === 'SC';
                  
                  // Calculate available tickets correctly (total - sold - held)
                  const availableTickets = Math.max(0, maxTickets - soldTickets - heldTickets);
                  const isSoldOut = availableTickets <= 0 || tempForceSoldOut;
                  const tooltipText = !onSale
                    ? `This package only goes on sale ${formatTooltipDate(pkg.startDate)}`
                    : isSoldOut
                      ? 'This package is sold out'
                      : '';

                  console.log('🔍 Package availability check:', {
                    packageType: pkg.packageType,
                    soldTickets,
                    heldTickets,
                    maxTickets,
                    totalSoldAndHeld: soldTickets + heldTickets,
                    availableTickets: maxTickets - soldTickets,
                    isSoldOut,
                    tempForceSoldOut,
                    onSale,
                    className: onSale
                      ? 'event-details-card__package'
                      : 'event-details-card__package event-details-card__package--disabled',
                    tooltipText
                  });

                  return (
                  <div
                    key={index}
                    className={
                      onSale
                        ? 'event-details-card__package'
                        : 'event-details-card__package event-details-card__package--disabled'
                    }
                    title={tooltipText}
                  >
                    <div className="event-details-card__package-header">
                      <h4 className="event-details-card__package-name">
                        {pkg.packageType || `Package ${index + 1}`}
                      </h4>
                      {pkg.numberOfTickets != null && (
                        <span className="event-details-card__package-tickets">
                          {(() => {
                            const soldTickets = pkg.soldTickets || pkg.sold_tickets || 0;
                            const heldTickets = pkg.heldTickets || pkg.held_tickets || 0;
                            const maxTickets = pkg.numberOfTickets || pkg.max_tickets || 0;
                            const availableTickets = Math.max(0, maxTickets - soldTickets - heldTickets);
                            
                            if (availableTickets <= 0) {
                              return 'Sold Out';
                            } else {
                              return `${availableTickets} tickets left`;
                            }
                          })()}
                        </span>
                      )}
                    </div>
                    {pkg.details && (
                      <div className="event-details-card__package-description">
                        {pkg.details}
                      </div>
                    )}
                    <div className="event-details-card__package-meta">
                      <div>Duration: {pkg.duration || 'TBA'} day(s)</div>
                      <div>
                        {pkg.validFromDate || 'TBA'} to {pkg.validToDate || 'TBA'}
                      </div>
                      <div>
                        {formatTime(pkg.validFromTime) || 'TBA'} to {formatTime(pkg.validToTime) || 'TBA'}
                      </div>
                    </div>
                    {pkg.pricing && (
                      <div className="event-details-card__package-price">
                        Price: {formatPrice(getDisplayPrice(pkg.pricing))}
                      </div>
                    )}

                    {showSelectButton && (
                    <button
                      type="button"
                      className="event-details-card__package-select-button"
                      onClick={
                        onSale && onSelectPackage
                          ? () => onSelectPackage(index)
                          : undefined
                      }
                      disabled={!onSale}
                    >
                      Select
                    </button>
                  )}
                  </div>
                  );
                })
              ) : (
                <p>No packages available for this event.</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 2 && (
          <div>
            <h3>Terms & Conditions</h3>
            <p className="event-details-card__description">
              {event.terms_and_conditions || 'No terms and conditions provided.'}
            </p>
          </div>
        )}

        {activeTab > 2 && (() => {
          const dynamicIndex = activeTab - baseTabs.length;
          const tab = dynamicTabs[dynamicIndex];

          if (!tab) return null;

          return (
            <div>
              <h3>{tab.name || 'Details'}</h3>
              <p className="event-details-card__description">
                {tab.content || 'No additional information.'}
              </p>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default ReadOnlyEventDetailsCard;
